import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Check, RectangleHorizontal, CircleDotDashed, Car, HelpCircle, Loader2, AlertCircle, CheckCircle2, Phone, ChevronDown } from 'lucide-react';
import { useLocations, PHONE_DISPLAY, PHONE_TEL } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { key: 'Windshield Replacement', label: 'Windshield\nReplacement', icon: RectangleHorizontal },
  { key: 'Rock Chip Repair', label: 'Stone Chip\nRepair', icon: CircleDotDashed },
  { key: 'Door / Back Glass', label: 'Door / Back\nGlass', icon: Car },
  { key: 'Not Sure', label: 'Not Sure', icon: HelpCircle },
];

const STEPS = ['Service', 'Vehicle', 'Details'];
const INITIAL = { service_type: '', vehicle: '', vin: '', full_name: '', phone: '', email: '', service_location: '', description: '' };

const slide = {
  initial: (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
  animate: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40 }),
};

export default function MobileQuoteSteps() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const locations = useLocations();
  const orleans = locations.find((l) => /orl/i.test(l.name || '')) || locations[0];

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); };

  const pickService = (key) => {
    set('service_type', key);
    setTimeout(() => go(1), 320);
  };

  const validateVehicle = () => {
    if (!form.vehicle.trim()) { setErrors({ vehicle: 'Enter your year, make and model' }); return false; }
    return true;
  };

  const validateDetails = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Your name is required';
    if (!/^[\d\s()+\-.]{7,}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.service_location) e.service_location = 'Pick where you\'d like service';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validateDetails()) return;
    setLoading(true); setSubmitError('');
    try {
      await axios.post(`${API}/public/quote-requests`, {
        full_name: form.full_name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        service_type: form.service_type, vehicle: form.vehicle.trim(), vin: form.vin.trim() || null,
        service_location: form.service_location, description: form.description.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Something went wrong. Please try again or call us.');
    } finally { setLoading(false); }
  };

  return (
    <section id="mobile-quote" data-testid="mobile-quote-section" className="bg-[#F4F6FA] px-5 pt-14 pb-16 scroll-mt-[72px]">
      <div className="flex items-start justify-between">
        <h2 className="text-[30px] leading-[1.05] font-black tracking-[-0.02em] text-[#111827]">Get Your Quote<br />in 3 Easy Steps</h2>
        <span className="font-hand text-[#0D5BD7] text-[19px] leading-[1.05] -rotate-6 text-right mt-1 shrink-0">Takes less than<br />2 minutes!</span>
      </div>

      <Progress step={step} done={success} />

      <div className="mt-6 bg-white rounded-3xl p-5 shadow-[0_10px_40px_rgba(15,23,42,0.08)] overflow-hidden">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          {success ? (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6" data-testid="mobile-quote-success">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }} className="mx-auto w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9" />
              </motion.div>
              <h3 className="mt-4 text-[22px] font-black text-[#111827]">You&apos;re all set!</h3>
              <p className="mt-2 text-[14px] text-[#5F6773]">We&apos;ll call or email you shortly with your quote. Need it faster?</p>
              <a href={PHONE_TEL} className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#111827] text-white text-[12px] font-bold tracking-[0.2em]">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </motion.div>
          ) : step === 0 ? (
            <motion.div key="s0" custom={dir} variants={slide} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
              <h3 className="text-[18px] font-bold text-[#111827]">What do you need?</h3>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {SERVICES.map((s) => {
                  const active = form.service_type === s.key;
                  return (
                    <motion.button
                      key={s.key}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => pickService(s.key)}
                      data-testid={`service-card-${s.key.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className={`relative h-[118px] rounded-2xl border-2 flex flex-col items-center justify-center gap-2.5 transition-colors duration-200 ${active ? 'border-[#0D5BD7] bg-[#EAF1FD]' : 'border-transparent bg-[#F4F6FA]'}`}
                    >
                      {active && <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>}
                      <s.icon className={`w-8 h-8 ${active ? 'text-[#0D5BD7]' : 'text-[#111827]'}`} strokeWidth={1.6} />
                      <span className="text-[13px] font-bold text-[#111827] leading-tight text-center whitespace-pre-line">{s.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : step === 1 ? (
            <motion.div key="s1" custom={dir} variants={slide} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
              <h3 className="text-[18px] font-bold text-[#111827]">Tell us about your vehicle</h3>
              <p className="mt-1 text-[13px] text-[#5F6773]">Year, make and model is all we need.</p>
              <Field label="Year / Make / Model" error={errors.vehicle}>
                <input value={form.vehicle} onChange={(e) => set('vehicle', e.target.value)} placeholder="e.g. 2022 Honda CR-V" className={inputCls(!!errors.vehicle)} data-testid="quote-vehicle-input" autoFocus />
              </Field>
              <Field label="VIN" hint="optional">
                <input value={form.vin} onChange={(e) => set('vin', e.target.value.toUpperCase())} placeholder="17 characters, if handy" maxLength={17} className={`${inputCls(false)} font-mono tracking-wide`} data-testid="quote-vin-input" />
              </Field>
              <NavButtons onBack={() => go(0)} onNext={() => validateVehicle() && go(2)} />
            </motion.div>
          ) : (
            <motion.div key="s2" custom={dir} variants={slide} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
              <h3 className="text-[18px] font-bold text-[#111827]">How can we reach you?</h3>
              <Field label="Full Name" error={errors.full_name}>
                <input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} placeholder="John Smith" autoComplete="name" className={inputCls(!!errors.full_name)} data-testid="quote-name-input" />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="613-555-0000" autoComplete="tel" className={inputCls(!!errors.phone)} data-testid="quote-phone-input" />
              </Field>
              <Field label="Email" error={errors.email}>
                <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" autoComplete="email" className={inputCls(!!errors.email)} data-testid="quote-email-input" />
              </Field>
              <Field label="Where should we service your vehicle?" error={errors.service_location}>
                <div className="relative">
                  <select
                    value={form.service_location}
                    onChange={(e) => set('service_location', e.target.value)}
                    data-testid="quote-location-select"
                    className={`${inputCls(!!errors.service_location)} appearance-none pr-11 ${form.service_location ? 'text-[#111827]' : 'text-slate-400'}`}
                  >
                    <option value="" disabled>Select an option</option>
                    <option value={orleans?.id || ''} disabled={!orleans}>A+ Auto Glass (Orleans)</option>
                    <option value="mobile">Come to me</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                </div>
              </Field>
              <Field label="Anything else?" hint="optional">
                <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} placeholder="Describe the damage or share a good time to call" className={`${inputCls(false)} h-auto py-3 resize-none`} data-testid="quote-description-input" />
              </Field>
              {submitError && (
                <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2.5 text-[13px] text-rose-700 flex gap-2" data-testid="quote-submit-error"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{submitError}</div>
              )}
              <NavButtons onBack={() => go(1)} onNext={submit} nextLabel={loading ? 'SENDING…' : 'GET MY QUOTE'} loading={loading} />
              <p className="mt-3 text-[11px] text-slate-400 text-center">By submitting, you agree to be contacted by A+ Auto Glass about your request.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Progress({ step, done }) {
  return (
    <div className="mt-7 flex items-start" data-testid="quote-progress">
      {STEPS.map((label, i) => {
        const complete = done || i < step;
        const active = !done && i === step;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center w-[64px]">
              <motion.div
                animate={{ scale: active ? 1.1 : 1 }}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold transition-colors duration-300 ${complete || active ? 'bg-[#0D5BD7] text-white shadow-[0_6px_16px_rgba(13,91,215,0.35)]' : 'bg-white border-2 border-slate-200 text-slate-400'}`}
              >
                {complete ? <Check className="w-4 h-4" strokeWidth={3} /> : i + 1}
              </motion.div>
              <span className={`mt-2 text-[12px] font-semibold ${active || complete ? 'text-[#111827]' : 'text-slate-400'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-[2px] mt-4 bg-slate-200 rounded overflow-hidden">
                <motion.div className="h-full bg-[#0D5BD7]" initial={false} animate={{ width: complete ? '100%' : '0%' }} transition={{ duration: 0.4 }} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel = 'NEXT', loading }) {
  return (
    <div className="mt-5 flex gap-3">
      <motion.button whileTap={{ scale: 0.95 }} type="button" onClick={onBack} data-testid="quote-back-btn" className="w-14 h-14 rounded-full border-2 border-slate-200 flex items-center justify-center text-[#111827] shrink-0">
        <ArrowLeft className="w-5 h-5" />
      </motion.button>
      <motion.button whileTap={{ scale: 0.97 }} type="button" onClick={onNext} disabled={loading} data-testid="quote-next-btn" className="flex-1 h-14 rounded-full bg-[#0D5BD7] disabled:opacity-70 text-white text-[13px] font-bold tracking-[0.2em] flex items-center justify-center gap-3 shadow-[0_10px_24px_rgba(13,91,215,0.35)]">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{nextLabel} <ArrowRight className="w-5 h-5" /></>}
      </motion.button>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <label className="text-[13px] font-semibold text-[#111827]">{label}</label>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
      {error && <div className="mt-1.5 flex items-center gap-1 text-[12px] text-rose-600"><AlertCircle className="w-3.5 h-3.5" />{error}</div>}
    </div>
  );
}

function inputCls(err) {
  return `w-full h-[52px] px-4 rounded-xl border-2 ${err ? 'border-rose-300 focus:border-rose-400' : 'border-slate-200 focus:border-[#0D5BD7]'} bg-white text-[15px] text-[#111827] placeholder:text-slate-400 focus:outline-none transition-colors`;
}
